// 警報清單補 phase / binding_id（VMS UX 評審第九輪 R9-P0-1、B9-01；SPEC #vms-web-290 第九輪收尾）——只給舊平台用：
// platform 2571097e 起 alarms 每筆帶 phase / binding_id / counted（lib/alarms hasEventCounts），listOpen 就不呼叫這裡（第十輪 B10-01）。
// 舊平台的 alarms 清單每筆沒帶 phase / binding_id，同一次的開始＋結束會被當成兩則——合併「× N 次」、
// 未處理數、徽章都是 facet 的兩倍。這裡用全文檢索（level:alarm、那批紀錄的時段）查回每筆的 phase 與 binding_id，
// 之後用清單合列的同一個配對函式算「次」。只查還不知道的；phase 不會變，記在記憶體（同一個 plane 的 event_id）。
// 平台有帶 phase 的筆直接用，不查。
import { searchApi, type SearchDoc, type SearchResponse } from './planeApi';
import type { AlarmItem } from './alarms';

type Known = { phase?: string; binding_id?: string } | null;

const cache = new Map<string, Known>();
const CACHE_MAX = 20_000;
/** 一次最多翻幾頁（200 一頁）；翻不完的下次再查 */
export const PHASE_MAX_PAGES = 10;
const PAGE = 200;
/** 索引追上警報清單要多久：這段時間內查不到的下次再查 */
const INDEX_LAG_MS = 120_000;

const isoS = (ms: number) => new Date(ms).toISOString().replace(/\.\d{3}Z$/, 'Z');

type SearchFn = (api: string, p: { q: string; homes: string[]; sort?: 'time' | 'relevance'; cursor?: string; limit?: number }) => Promise<SearchResponse>;

/** 測試用 */
export function resetAlarmPhaseCache(): void {
  cache.clear();
}

function put(api: string, d: Pick<SearchDoc, 'event_id' | 'phase' | 'binding_id'>) {
  cache.set(`${api}|${d.event_id}`, { phase: d.phase || undefined, binding_id: d.binding_id || undefined });
  if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
}

/**
 * 回傳同樣順序的清單，每筆補上 phase / binding_id（查得到的話）。查詢失敗照原樣回（次數退回筆數，不擋畫面）。
 */
export async function withAlarmPhases(api: string, homeId: string, items: AlarmItem[], search: SearchFn = searchApi.events, now = Date.now()): Promise<AlarmItem[]> {
  const need = items.filter((a) => !a.phase && !cache.has(`${api}|${a.event_id}`));
  if (need.length) {
    const ts = need.map((a) => Date.parse(a.occurred_at)).filter(Number.isFinite);
    if (ts.length) {
      const want = new Set(need.map((a) => a.event_id));
      const q = `level:alarm after:${isoS(Math.min(...ts) - 1000)} before:${isoS(Math.max(...ts) + 2000)}`;
      let cursor: string | undefined;
      let pages = 0;
      let done = false;
      try {
        do {
          const r = await search(api, { q, homes: [homeId], sort: 'time', limit: PAGE, ...(cursor ? { cursor } : {}) });
          for (const d of r?.data ?? []) {
            put(api, d);
            want.delete(d.event_id);
          }
          cursor = r?.next_cursor || undefined;
          pages += 1;
          done = !cursor;
        } while (cursor && want.size && pages < PHASE_MAX_PAGES);
        // 翻完了還沒找到的：索引裡沒有（或沒有 phase），記成「沒有」，下次不再查——最近 2 分鐘的不記（索引可能還沒跟上）
        const recent = new Set(need.filter((a) => now - (Date.parse(a.occurred_at) || 0) < INDEX_LAG_MS).map((a) => a.event_id));
        if (done || !want.size) for (const id of want) if (!recent.has(id)) cache.set(`${api}|${id}`, null);
      } catch {
        // 讀不到就照原樣（下次再試）
      }
    }
  }
  return items.map((a) => {
    if (a.phase) return a;
    const k = cache.get(`${api}|${a.event_id}`);
    return k ? { ...a, ...(k.phase ? { phase: k.phase } : {}), ...(k.binding_id ? { binding_id: k.binding_id } : {}) } : a;
  });
}
